import { useVerifyProjectOwnershipMutation } from '../../services/projectDataApi'
import { useEffect, useRef, useState } from 'react'
import { ShieldCheck, CheckCircle2, Copy, Check, ClipboardCopy, X } from 'lucide-react'
import Spinner from '../Spinner'
import ModalPortal from '../ModalPortal'
import {
  buildSecurityAgentPrompt,
  nonceFailureMessage,
  securityNoncePublishUrl,
  verifySecurityNonce,
  type NonceCheckResult,
} from '../../lib/securityNonce'

// Ownership setup for the Security pass. The active probe sends real attack traffic — path traversal,
// IDOR, injection — so it only ever runs against an origin that echoes a value we minted for this
// project. This is where the user gets that value and confirms it is live.
//
// Deterministic on purpose: the path and the value have to be exact, and confirming them is a server
// round trip with a retry loop. What varies is their stack, and the thing that actually knows it is
// their own coding agent — hence the copyable prompt rather than a support conversation with ours.
//
// Split in two because the two places it appears want different weight. In the transcript it is one
// line that opens a dialog: the instructions are a page of text, and rendered inline they filled the
// chat body and pushed the conversation out of view. In project settings, where the user has already
// clicked into a disclosure to see it, the same body renders inline — settings itself is sometimes a
// modal, and a dialog on top of a dialog is worse than the height.

export interface SecurityNonceState {
  nonce: string
  verified: boolean
  /** Account-level waiver: this account is vouched for, so no file is needed. */
  waived: boolean
}

/** The instructions themselves: what to publish, where, and a live check. */
export function SecurityNonceSetup({
  projectId,
  targetUrl,
  nonce,
  verified,
  onResult,
}: {
  projectId?: string
  targetUrl: string
  nonce: string
  verified: boolean
  onResult?: (state: SecurityNonceState) => void
}) {
  const [verifyProjectOwnership] = useVerifyProjectOwnershipMutation()
  const [checking, setChecking] = useState(false)
  const [lastResult, setLastResult] = useState<NonceCheckResult | null>(null)
  const [copied, setCopied] = useState<'value' | 'prompt' | null>(null)
  const publishUrl = securityNoncePublishUrl(targetUrl)

  async function copy(what: 'value' | 'prompt') {
    const text = what === 'value' ? nonce : buildSecurityAgentPrompt(targetUrl, nonce)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 1800)
    } catch {
      // Clipboard denied or unavailable: leave the button ready to retry rather than reporting a copy
      // that did not happen. Both values are visible above, so nothing is unreachable.
    }
  }

  async function check() {
    if (!nonce || checking) return
    setChecking(true)
    try {
      const result = projectId
        ? await verifyProjectOwnership(projectId).unwrap()
        : await verifySecurityNonce(targetUrl, nonce)
      setLastResult(result)
      onResult?.({ nonce, verified: result.verified, waived: false })
    } catch {
      setLastResult({ url: publishUrl, verified: false, reason: 'unreachable' })
    } finally {
      setChecking(false)
    }
  }

  const failed = lastResult && !lastResult.verified

  return (
    <div className="sec-setup" data-testid="security-nonce-setup">
      <p className="sec-setup__lead">
        Verify that you control this app using its capture installation or the ownership file below. Check now looks
        for this project’s Self Healing capture key in the deployed app and its JavaScript bundles, or checks the nonce file.
      </p>

      {/* The fastest path first. Publishing this file is a change to their repo, which their own coding
          agent can make and ours cannot, so hand over a finished brief instead of asking a person to
          relay one. The manual steps stay below for anyone editing a config by hand. */}
      <div className="sec-setup__handoff">
        <div className="sec-setup__handoff-text">
          <span className="sec-setup__step-title">Fastest way: let your coding agent do it</span>
          <span className="sec-setup__step-note">
            Copies a ready-made prompt — the exact path, the value, and the rewrite and auth rules it has
            to respect. Paste it into Cursor, Claude Code, or whatever you use.
          </span>
        </div>
        <button
          type="button"
          className="rp-card__copy sec-setup__handoff-btn"
          onClick={() => void copy('prompt')}
          data-testid="chat-security-nonce-copy-prompt"
          data-analytics-event="chat-security-nonce-copy-prompt"
        >
          {copied === 'prompt' ? <Check size={13} aria-hidden /> : <ClipboardCopy size={13} aria-hidden />}
          {copied === 'prompt' ? 'Prompt copied' : 'Copy prompt'}
        </button>
      </div>

      <p className="sec-setup__or">Or do it yourself:</p>

      <ol className="sec-setup__steps">
        <li>
          <span className="sec-setup__step-title">Create a plain text file</span> whose entire contents are
          this value — no quotes, no surrounding HTML or JSON:
          <code className="sec-setup__nonce" data-testid="chat-security-nonce-value">{nonce}</code>
          <button
            type="button"
            className="rp-card__copy sec-setup__inline-btn"
            onClick={() => void copy('value')}
            data-testid="chat-security-nonce-copy"
            data-analytics-event="chat-security-nonce-copy"
          >
            {copied === 'value' ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
            {copied === 'value' ? 'Copied' : 'Copy value'}
          </button>
        </li>
        <li>
          <span className="sec-setup__step-title">Serve it at this exact URL</span> — same origin as your
          app, reachable without logging in, returning 200 rather than a redirect:
          <code className="sec-setup__path">{publishUrl}</code>
          <span className="sec-setup__step-note">
            On most frameworks that is a file at <code>public/.well-known/replay-qa-security-nonce</code>.
            The path is fixed; we look nowhere else.
          </span>
        </li>
        <li>
          <span className="sec-setup__step-title">Deploy, then press Check now.</span> We fetch that URL and
          look for the value. Nothing is sent to your app until it matches.
        </li>
      </ol>

      <details className="sec-setup__more">
        <summary data-testid="chat-security-nonce-more">Where does this go on my stack?</summary>
        <ul className="sec-setup__hosts">
          <li><strong>Next.js, Vite, CRA, Astro, SvelteKit, Nuxt, Remix</strong> — add the file at <code>public/.well-known/replay-qa-security-nonce</code> (no extension) and deploy.</li>
          <li><strong>Rails, Laravel, Django, Phoenix</strong> — same path under your public or static root, e.g. <code>public/.well-known/</code> or <code>static/.well-known/</code>.</li>
          <li><strong>Netlify, Vercel, Cloudflare Pages, S3</strong> — it ships with your built output. Check the dot-directory isn't dropped by <code>.gitignore</code> or your build's copy step.</li>
          <li><strong>nginx</strong> — a <code>location = /.well-known/replay-qa-security-nonce</code> block returning the value as <code>text/plain</code>.</li>
          <li><strong>Express, Fastify, Flask</strong> — a route returning the value as <code>text/plain</code>, registered before any auth middleware.</li>
        </ul>
        <p className="sec-setup__gotcha">
          Two things account for almost every failed check, and both return 200 with the wrong body: a
          catch-all or SPA rewrite serving <code>index.html</code> for unknown paths, and auth or bot
          protection in front of every route. This one path has to be excluded from both. The copied prompt
          covers them; if the check still fails it tells you which one you hit.
        </p>
      </details>

      <div className="sec-setup__actions">
        <button
          type="button"
          className="rp-card__copy sec-setup__check"
          onClick={() => void check()}
          disabled={checking}
          data-testid="chat-security-nonce-check"
          data-analytics-event="chat-security-nonce-check"
        >
          {checking ? <Spinner size={13} /> : <ShieldCheck size={13} aria-hidden />}
          {checking ? 'Checking…' : 'Check now'}
        </button>
      </div>

      {verified ? (
        <p className="sec-setup__result sec-setup__result--ok" data-testid="chat-security-nonce-ok">
          <CheckCircle2 size={14} aria-hidden /> Ownership confirmed. Keep Self Healing installed or the ownership file in place — ownership is checked
          again at the start of every run.
        </p>
      ) : failed ? (
        <p className="sec-setup__result sec-setup__result--warn" data-testid="chat-security-nonce-error">
          {projectId && 'Neither the deployed capture key nor the ownership file could be verified. '}{nonceFailureMessage(lastResult)}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The transcript's presence: a single line, plus a dialog holding the instructions above.
 *
 * Takes the nonce rather than minting one, so it can also render against a project's stored value
 * (project chat, where the pass was already turned on) without handing out a second nonce that would
 * invalidate whatever the user already published.
 */
export function SecurityOwnershipCard({
  projectId,
  targetUrl,
  value,
  onChange,
  onSkip,
}: {
  projectId?: string
  targetUrl: string
  value: SecurityNonceState
  onChange: (next: SecurityNonceState) => void
  /** Dismisses the step without enabling security. Omitted after the project exists. */
  onSkip?: () => void
}) {
  const [open, setOpen] = useState(false)
  const autoOpened = useRef(false)

  const nonce = value.nonce
  const waived = value.waived === true
  const verified = value.verified === true

  // Open once, as soon as there is a nonce to show. The user just asked for security testing, so the
  // instructions are the answer to what they clicked — waiting behind a second button made the step
  // easy to walk straight past. Only ever automatic once: dismissing it leaves the line in the
  // transcript to come back to.
  useEffect(() => {
    if (autoOpened.current || !nonce || verified || waived) return
    autoOpened.current = true
    setOpen(true)
  }, [nonce, verified, waived])

  // Close the dialog the moment the check passes: the instructions have nothing left to say, and the
  // line underneath now reads as confirmed.
  useEffect(() => { if (verified || waived) setOpen(false) }, [verified, waived])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (waived || verified) {
    return (
      <div className="rp-card rp-card--ready" data-testid="chat-security-nonce" data-status={waived ? 'waived' : 'verified'}>
        <CheckCircle2 size={15} className="rp-card__icon rp-card__icon--ok" aria-hidden />
        <div className="rp-card__main">
          <span className="rp-card__title">{waived ? 'Security testing enabled' : 'Ownership confirmed'}</span>
          <span className="rp-card__text">
            {waived
              ? 'Your account is approved for security testing, so there is nothing to publish.'
              : 'Security testing is on for this app. Keep that file in place — it is checked again before every run.'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Marked as unfinished business rather than a neutral note: this is the one thing standing
          between the user and the pass they just asked for, and a grey row alongside grey chat read as
          decoration people scrolled past. */}
      <div className="rp-card sec-line sec-line--todo" data-testid="chat-security-nonce" data-status="awaiting">
        <ShieldCheck size={15} className="rp-card__icon sec-line__icon" aria-hidden />
        <div className="rp-card__main">
          <span className="rp-card__title">
            Confirm you own this app
            <span className="sec-line__badge">Not finished</span>
          </span>
          <span className="rp-card__text">Confirm ownership to view security findings and complete testing.</span>
        </div>
        <div className="sec-line__actions">
          <button
            type="button"
            className="rp-card__copy sec-line__primary"
            onClick={() => setOpen(true)}
            data-testid="chat-security-nonce-open"
            data-analytics-event="chat-security-nonce-open"
          >
            Show me how
          </button>
          {onSkip && (
            <button
              type="button"
              className="rp-card__skip"
              onClick={onSkip}
              data-testid="chat-security-nonce-skip"
              data-analytics-event="chat-security-nonce-skip"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>

      {open && (
        <ModalPortal>
          <div
            className="sec-dialog__overlay"
            data-testid="security-nonce-dialog"
            onClick={() => setOpen(false)}
          >
            <div
              className="sec-dialog"
              role="dialog"
              aria-modal="true"
              aria-label="Confirm you own this app"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sec-dialog__head">
                <ShieldCheck size={17} aria-hidden />
                <h2 className="sec-dialog__title">Confirm you own this app</h2>
                <button
                  type="button"
                  className="sec-dialog__close"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="chat-security-nonce-close"
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
              <SecurityNonceSetup
                projectId={projectId}
                targetUrl={targetUrl}
                nonce={nonce}
                verified={verified}
                onResult={onChange}
              />
              {/* Leaving with nothing published is a normal outcome, not a failure: the file usually
                  needs a deploy. The step is finished from project settings later. */}
              <p className="sec-dialog__foot">
                You can start QA now. Security scanning runs automatically during testing; confirm ownership to view findings and complete testing.
              </p>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  )
}
